import React, { useState, useEffect } from 'react';
import FallbackHeaderImage from './FallbackHeaderImage';
import {
  hasImageVariants,
  imageSrcSet,
  imageUrl,
  imageVariantUrl,
  imageVersionFor,
  VARIANT_FALLBACK_FORMAT,
} from '../utils/imageUrl';
import { UNBRANDED_THEME } from '../styling/unbrandedTheme.js';

/**
 * The hosted hero's rendered width, measured on `HostedNormalLayout` under the
 * donor mocks (CON-72 pre-check 3, 2026-09-15): 358 px at a 390 viewport and
 * 712 at 768 (one column — `100vw` holds); on the desktop layout the left rail
 * is fluid beside a fixed right panel — 488 at 1024, 728 at 1280, 888 at 1440 —
 * and the page container caps it at 960 from about 1512 up. The browser reads
 * this to pick a `srcset` candidate BEFORE layout, so it must describe the
 * layout rather than follow it.
 */
const HERO_IMAGE_SIZES =
  '(max-width: 1023px) 100vw, (max-width: 1279px) calc(100vw - 536px), (max-width: 1511px) calc(100vw - 552px), 960px';

/** The middle variant: what a `<picture>`-less UA and the `src` attribute fall back to. */
const FALLBACK_VARIANT_WIDTH = 960;

/**
 * HeaderImage — Shared campaign header image with fallback chain.
 *
 * Renders a 16:9 aspect-ratio image with:
 * 1. Variant `<picture>` (CON-72) — WebP `<source srcset sizes>` + JPEG
 *    `<img srcset sizes>` over the CON-70 widths, ONLY when the record carries a
 *    real `imageVersion` (the one signal that variants may exist)
 * 2. Primary source — the plain original (`.png`, the URL CON-69 builds), on
 *    the variant's first error or when no version is present
 * 3. Secondary retry (same URL, handles transient errors)
 * 4. FallbackHeaderImage SVG (brand-colored generative art)
 *
 * Used by CampaignDetailHeader (shared) which is consumed by:
 * - hosted-page CampaignHeader (LeftPanel)
 * - donation-elements OverlayCampaignDetail (overlay mobile)
 *
 * @param {string} imageBaseUrl - Base URL for image hosting (e.g. VITE_GLOBAL_IMAGE_URL)
 * @param {string} merchantName - Nonprofit name for image path
 * @param {string} formID - Form ID for image filename
 * @param {string} formType - 'EVENT' | other — determines image folder
 * @param {string} [alt='Fundraiser Header'] - Image alt text
 * @param {string} [className=''] - Additional container classes
 * @param {string} [headerImage] - Direct image URL (preview mode)
 * @param {boolean} [previewMode=false] - Whether in dashboard preview
 * @param {string} [brandColor] - Hex color for fallback SVG
 * @param {string|number} [imageVersion] - Persisted version (CON-69); gates the variant path
 * @param {string} [sizes] - The `sizes` attribute for the variant path. Defaults to the
 *   hosted rail's measured layout; the widget passes a viewport-relative value because the
 *   host page, not this component, decides how wide the iframe is.
 */
const HeaderImage = ({
  imageBaseUrl,
  merchantName,
  formID,
  formType,
  alt = 'Fundraiser Header',
  className = '',
  headerImage,
  previewMode = false,
  brandColor,
  title, // the campaign's name, for the typographic cover
  imageVersion,
  sizes = HERO_IMAGE_SIZES,
}) => {
  const [variantImageError, setVariantImageError] = useState(false);
  const [primaryImageError, setPrimaryImageError] = useState(false);

  // Helper to ensure brandColor is a valid hex string. Without one, the
  // fallback art takes the neutral action fill (CON-331).
  const ensureValidHexColor = (color) => {
    if (!color || typeof color !== 'string') return UNBRANDED_THEME.lightColor;
    const trimmed = color.trim();
    if (!trimmed) return UNBRANDED_THEME.lightColor;
    const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (!/^#[0-9A-Fa-f]{3}$|^#[0-9A-Fa-f]{6}$/.test(normalized)) {
      return UNBRANDED_THEME.lightColor;
    }
    return normalized;
  };

  // Reset error states when headerImage changes in preview mode
  useEffect(() => {
    if (previewMode && headerImage !== undefined) {
      setVariantImageError(false);
      setPrimaryImageError(false);
    }
  }, [previewMode, headerImage]);

  const containerClasses = 'aspect-video w-full relative shrink-0';
  // CON-518 (SPEC §C): the hero is a card-radius surface with a 1px edge on the sunken
  // ground while it loads, and no shadow (only cards, menus and sheets carry one).
  const imageClasses =
    'absolute inset-0 h-full w-full object-cover rounded-[var(--cs-card-radius)] bg-[color:var(--cs-surface-sunken)]';
  const imageStyles = {
    border: 'var(--header-image-border, 1px solid var(--cs-border-control))',
  };
  const containerStyles = {
    aspectRatio: '16 / 9',
  };

  // Attributes every hero <img> carries. The wrapper sizes the image (absolute,
  // inset-0, full width and height), so `width`/`height` only declare the
  // intrinsic 16:9 ratio — the client cap CON-70 encodes to — and change no
  // layout. `fetchpriority` is spelled lowercase on purpose: React 18.3 has no
  // `fetchPriority` DOM property, so the camelCase prop would warn and be
  // dropped, while the lowercase attribute passes straight through.
  const heroImageAttributes = {
    className: imageClasses,
    style: imageStyles,
    loading: 'eager',
    decoding: 'async',
    width: 1920,
    height: 1080,
    fetchpriority: 'high',
  };

  const folder = formType === 'EVENT' ? 'event' : 'fundraiser';
  const imageSource = {
    base: imageBaseUrl,
    type: folder,
    name: `${merchantName}_${formID}`,
    version: imageVersionFor({ imageVersion }),
  };

  const buildImageSrc = () => {
    if (previewMode) return headerImage;
    return imageUrl(imageSource);
  };

  // The variant path needs a real version (deviation A: the only signal that
  // CON-70 may have written variants) and a base to build URLs against.
  const showVariants =
    !previewMode &&
    !variantImageError &&
    Boolean(imageBaseUrl) &&
    hasImageVariants({ imageVersion });

  // Show fallback immediately if in preview mode and no image
  if (previewMode && (!headerImage || headerImage === '')) {
    const fallbackBrandColor = ensureValidHexColor(brandColor);
    return (
      <div className={`${containerClasses} ${className}`} style={containerStyles}>
        <FallbackHeaderImage alt="Default fundraiser image: heart-shaped forest" title={title} brandColor={fallbackBrandColor} />
      </div>
    );
  }

  if (!primaryImageError) {
    if (showVariants) {
      return (
        <div className={`${containerClasses} ${className}`} style={containerStyles}>
          <picture>
            <source
              type="image/webp"
              srcSet={imageSrcSet({ ...imageSource, format: 'webp' })}
              sizes={sizes}
            />
            <img
              {...heroImageAttributes}
              alt={alt}
              src={imageVariantUrl({
                ...imageSource,
                width: FALLBACK_VARIANT_WIDTH,
                format: VARIANT_FALLBACK_FORMAT,
              })}
              srcSet={imageSrcSet({ ...imageSource, format: VARIANT_FALLBACK_FORMAT })}
              sizes={sizes}
              onError={() => setVariantImageError(true)}
            />
          </picture>
        </div>
      );
    }

    return (
      <div className={`${containerClasses} ${className}`} style={containerStyles}>
        <img
          {...heroImageAttributes}
          alt={alt}
          src={buildImageSrc()}
          onError={() => setPrimaryImageError(true)}
          key={previewMode ? headerImage : undefined}
        />
      </div>
    );
  }

  // In preview mode, skip secondary fallback and go directly to fallback image
  if (previewMode) {
    const fallbackBrandColor = ensureValidHexColor(brandColor);
    return (
      <div className={`${containerClasses} ${className}`} style={containerStyles}>
        <FallbackHeaderImage alt="Default fundraiser image: heart-shaped forest" title={title} brandColor={fallbackBrandColor} />
      </div>
    );
  }

  // No second attempt here: it would request the same stable versioned URL
  // (`?v=<version>`) as the load that just failed, so React would keep the same
  // <img>, nothing would be re-requested, no second error would fire, and a
  // campaign with no photo would sit on a broken image instead of reaching the
  // brand fallback below. (The old retry worked only because the URL carried a
  // per-render timestamp.)

  return (
    <div className={`${containerClasses} ${className}`} style={containerStyles}>
      <FallbackHeaderImage alt="Default fundraiser image: heart-shaped forest" title={title} brandColor={ensureValidHexColor(brandColor)} />
    </div>
  );
};

export default HeaderImage;
